import { OUTLINE_DEFAULT_COLUMN_WIDTH } from "../../constants";
import { AutoAdjustBackwardsLimit } from "../../constants/autoAdjustBackwardsLimit";
import { AutoAdjustDateLevel } from "../../constants/autoAdjustDateLevel";
import compactDisplays from "../../constants/compactDisplay";
import defaultGenericNarrativeLabels from "../../constants/defaultGenericNarrativeLabels";
import { SPREADSHEET_VIEW } from "../../constants/identifiers";
import itemTypeCategories from "../../constants/itemTypeCategories";
import itemTypeIds from "../../constants/itemTypeIds";
import narrativeNumberingOptions from "../../constants/narrativeNumberingOptions";
import outlineColumnIds from "../../constants/outlineColumnIds";
import { REFERENCE_RELATES_TO } from "../../constants/references";
import subwayDisplayOptions from "../../constants/subwayDisplayOptions";
import { TimelineZoomLimits } from "../../constants/zoomRange";
import { getGregorianCalendarObject } from "../../modules/definition/calendar/gregorianCalendarRaw";
import { defaultColumnsForSpreadsheetView } from "../../types/immutableRecords/model/appState/defaultColumnsForSpreadsheetView";
import arrayToListObjectCamelCase from "../../utils/arrayToListObjectCamelCase";

const calendar = getGregorianCalendarObject();

const spreadsheetColumns = defaultColumnsForSpreadsheetView
  .push(
    {
      id: outlineColumnIds.blocks,
      width: OUTLINE_DEFAULT_COLUMN_WIDTH
    },
    {
      id: outlineColumnIds.blockedBy,
      width: OUTLINE_DEFAULT_COLUMN_WIDTH
    }
  )
  .toJS();

const defaultColumns = {};
defaultColumns[SPREADSHEET_VIEW] = spreadsheetColumns;

export default {
  autoAdjustLevel: AutoAdjustDateLevel.ResolveConstraints,
  autoAdjustBackwardsLimit: AutoAdjustBackwardsLimit.Today,
  includeNarrative: false,
  types: {
    byId: {
      client: {
        enabled: true,
        canDelete: true,
        id: "client",
        label: "Client",
        pluralLabel: "Client",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO, "leadRef", "teamRef", "documentRef", "contactRef"],
        properties: ["notes", "budget", "address", "email", "phone"],
        superTypes: [],
        icon: "people",
        color: "iconRed",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "CLI",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      sessionItem: {
        enabled: true,
        canDelete: true,
        id: "sessionItem",
        label: "Session",
        pluralLabel: "Sessions",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "assignedRef",
          "locationRef",
          "supervisorRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO,
          "documentRef",
          "projectRef",
          "clientRef",
          "vendorRef",
          "teamRef"
        ],
        properties: ["notes", "priority", "budget", "cost", "address"],
        superTypes: ["meeting", "eventItem", itemTypeIds.EVENT_ITEM_TYPE_ID, "sessionItem"],
        icon: "star",
        color: "iconRed",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "MS",
        compactDisplay: compactDisplays.IDENTIFIER,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.NARRATIVE_FOLDER_TYPE_ID]: {
        enabled: false,
        canDelete: false,
        id: itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
        label: "Narrative Folder",
        pluralLabel: "Narrative Folders",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO],
        properties: [],
        superTypes: [],
        icon: "folder",
        color: "iconMono",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: true,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "NF",
        compactDisplay: compactDisplays.IDENTIFIER,
        isNarrativeFolder: true,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      vendor: {
        enabled: true,
        canDelete: true,
        id: "vendor",
        label: "Vendor",
        pluralLabel: "Vendors",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO, "leadRef", "teamRef", "documentRef", "contactRef"],
        properties: ["notes", "budget", "address", "email", "phone"],
        superTypes: [],
        icon: "puzzle",
        color: "iconPurple",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "VEN",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.PERSON_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.PERSON_ITEM_TYPE_ID,
        label: "Person",
        pluralLabel: "People",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Birthday",
        extendAnniversaryBeyondEnd: false,
        references: [REFERENCE_RELATES_TO, "supervisorRef"],
        properties: ["notes", "email", "phone", "jobTitle", "address"],
        superTypes: [],
        icon: "person",
        color: "iconGreen",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "PER",
        compactDisplay: compactDisplays.AVATAR_INITIALS,
        category: itemTypeCategories.PERSON,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.LOCATION_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.LOCATION_ITEM_TYPE_ID,
        label: "Location",
        pluralLabel: "Locations",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO, "teamRef", "documentRef"],
        properties: ["notes", "address", "cost", "phone", "email"],
        superTypes: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "map",
        color: "iconOrange",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "LOC",
        compactDisplay: compactDisplays.SHORT_LABEL,
        category: itemTypeCategories.LOCATION,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.MINDMAP_NODE_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.MINDMAP_NODE_TYPE_ID,
        label: "Mindmap Node",
        pluralLabel: "Nodes",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: [],
        icon: "diamond",
        color: "iconMono",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "NO",
        compactDisplay: compactDisplays.IDENTIFIER,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.EVENT_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.EVENT_ITEM_TYPE_ID,
        label: "Task",
        pluralLabel: "Tasks",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "assignedRef",
          "locationRef",
          "supervisorRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO,
          "documentRef",
          "projectRef",
          "clientRef",
          "vendorRef",
          "teamRef"
        ],
        properties: ["notes", "status", "cost", "budget", "priority", "address"],
        superTypes: ["meeting", itemTypeIds.EVENT_ITEM_TYPE_ID, "sessionItem", "eventItem"],
        icon: "checkedBox",
        color: "iconPurple",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "TSK",
        compactDisplay: compactDisplays.IDENTIFIER,
        isNarrativeFolder: false,
        defaultSequenceType: true,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      document: {
        enabled: true,
        canDelete: true,
        id: "document",
        label: "Document",
        pluralLabel: "Documents",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO, "clientRef", "vendorRef"],
        properties: ["notes"],
        superTypes: ["document"],
        icon: "people",
        color: "iconYellow",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "DEP",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      contract: {
        enabled: true,
        canDelete: true,
        id: "contract",
        label: "Contract",
        pluralLabel: "Contracts",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO, "clientRef", "vendorRef"],
        properties: ["notes", "cost", "budget"],
        superTypes: [],
        icon: "document",
        color: "iconGreen",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "CON",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      meeting: {
        enabled: true,
        canDelete: true,
        id: "meeting",
        label: "Meeting",
        pluralLabel: "Meetings",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "assignedRef",
          "supervisorRef",
          "projectRef",
          "locationRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO,
          "documentRef",
          "clientRef",
          "vendorRef",
          "teamRef"
        ],
        properties: ["budget", "notes", "cost", "priority", "address"],
        superTypes: ["meeting", itemTypeIds.EVENT_ITEM_TYPE_ID, "eventItem", "sessionItem"],
        icon: "comments",
        color: "iconYellow",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "PRJ",
        compactDisplay: compactDisplays.IDENTIFIER,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      eventItem: {
        enabled: true,
        canDelete: true,
        id: "eventItem",
        label: "Event",
        pluralLabel: "Events",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          REFERENCE_RELATES_TO,
          "assignedRef",
          "locationRef",
          "projectRef",
          "seeAlsoRef",
          "supervisorRef",
          "clientRef",
          "vendorRef",
          "teamRef",
          "documentRef"
        ],
        properties: ["notes", "budget", "cost", "priority", "address"],
        superTypes: ["eventItem", itemTypeIds.EVENT_ITEM_TYPE_ID, "sessionItem", "meeting"],
        icon: "diamond",
        color: "iconMono",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "EV",
        compactDisplay: compactDisplays.IDENTIFIER,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      }
    },
    allIds: [
      itemTypeIds.EVENT_ITEM_TYPE_ID,
      "eventItem",
      "meeting",
      "sessionItem",
      "client",
      "vendor",
      itemTypeIds.PERSON_ITEM_TYPE_ID,
      itemTypeIds.LOCATION_ITEM_TYPE_ID,
      "contract",
      "document",
      itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
      itemTypeIds.MINDMAP_NODE_TYPE_ID
    ]
  },
  properties: {
    byId: {
      notes: {
        enabled: true,
        canDelete: true,
        id: "notes",
        key: "notes",
        label: "Notes",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      priority: {
        enabled: true,
        canDelete: true,
        id: "priority",
        key: "priority",
        label: "Priority",
        format: "list",
        allowed: arrayToListObjectCamelCase(["High", "Medium", "Low"]),
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      budget: {
        enabled: true,
        canDelete: true,
        id: "budget",
        key: "budget",
        label: "Budget",
        format: "currency",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      cost: {
        enabled: true,
        canDelete: true,
        id: "cost",
        key: "cost",
        label: "Cost",
        format: "currency",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      phone: {
        enabled: true,
        canDelete: true,
        id: "phone",
        key: "phone",
        label: "Phone",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      status: {
        enabled: true,
        canDelete: true,
        id: "status",
        key: "status",
        label: "Status",
        format: "list",
        allowed: arrayToListObjectCamelCase(["Not started", "In progress", "Complete", "Needs attention"]),
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      address: {
        enabled: true,
        canDelete: true,
        id: "address",
        key: "address",
        label: "Address",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      jobTitle: {
        enabled: true,
        canDelete: true,
        id: "jobTitle",
        key: "jobTitle",
        label: "Job Title",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      email: {
        enabled: true,
        canDelete: true,
        id: "email",
        key: "email",
        label: "Email",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      }
    },
    allIds: ["notes", "status", "budget", "cost", "priority", "address", "email", "phone", "jobTitle"]
  },
  references: {
    byId: {
      clientRef: {
        enabled: true,
        canDelete: true,
        id: "clientRef",
        key: null,
        label: "Client",
        types: ["client"],
        icon: "circle",
        color: "mint",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      contactRef: {
        enabled: true,
        canDelete: true,
        id: "contactRef",
        key: null,
        label: "Contact",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "blue300",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Contact For",
        showInverse: true,
        showInInspector: false
      },
      assignedRef: {
        enabled: true,
        canDelete: true,
        id: "assignedRef",
        key: "assignedRef",
        label: "Assigned",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "lightBlue",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Assigned to",
        showInverse: true,
        showInInspector: true
      },
      vendorRef: {
        enabled: true,
        canDelete: true,
        id: "vendorRef",
        key: null,
        label: "Vendor",
        types: ["vendor"],
        icon: "circle",
        color: "lightPurple",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      supervisorRef: {
        enabled: true,
        canDelete: true,
        id: "supervisorRef",
        key: "supervisor",
        label: "Supervisor",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle filled",
        color: "midBlue",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      documentRef: {
        enabled: true,
        canDelete: true,
        id: "documentRef",
        key: "documentRef",
        label: "Document",
        types: ["document"],
        icon: "circle",
        color: "yellow",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      teamRef: {
        enabled: true,
        canDelete: true,
        id: "teamRef",
        key: "teamRef",
        label: "Contract",
        types: ["contract"],
        icon: "circle",
        color: "fuschia",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      leadRef: {
        enabled: true,
        canDelete: true,
        id: "leadRef",
        key: "leadRef",
        label: "Employs",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "lightGreen500",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Employed By",
        showInverse: true,
        showInInspector: false
      },
      seeAlsoRef: {
        enabled: true,
        canDelete: true,
        id: "seeAlsoRef",
        key: "seeAlsoRef",
        label: "See also",
        types: ["meeting", itemTypeIds.EVENT_ITEM_TYPE_ID, "sessionItem", "eventItem"],
        icon: "circle",
        color: "mono",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      projectRef: {
        enabled: true,
        canDelete: true,
        id: "projectRef",
        key: "projectRef",
        label: "Venue",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle filled",
        color: "peach",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Venue",
        showInverse: true,
        showInInspector: true
      },
      locationRef: {
        enabled: true,
        canDelete: true,
        id: "locationRef",
        key: "locationRef",
        label: "Location",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle filled",
        color: "red",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      [REFERENCE_RELATES_TO]: {
        enabled: true,
        canDelete: false,
        id: REFERENCE_RELATES_TO,
        key: REFERENCE_RELATES_TO,
        label: "Relates to",
        types: [
          itemTypeIds.MINDMAP_NODE_TYPE_ID,
          "meeting",
          itemTypeIds.EVENT_ITEM_TYPE_ID,
          "sessionItem",
          itemTypeIds.PERSON_ITEM_TYPE_ID,
          itemTypeIds.LOCATION_ITEM_TYPE_ID,
          "document",
          itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
          "client",
          "eventItem",
          "contract",
          "vendor"
        ],
        icon: "circle",
        color: "mono",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      }
    },
    allIds: [
      "assignedRef",
      "locationRef",
      "projectRef",
      "seeAlsoRef",
      "supervisorRef",
      "leadRef",
      "clientRef",
      "vendorRef",
      "teamRef",
      "documentRef",
      "contactRef",
      REFERENCE_RELATES_TO
    ]
  },
  calendar,
  colors: {
    byId: {
      "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC": {
        id: "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
        label: "Light Purple",
        swatchColor: "lightPurple"
      },
      "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5": {
        id: "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
        label: "Light Teal",
        swatchColor: "lightTeal"
      },
      "88F01362-D7BA-4AD9-8C93-E0EC45A604B4": {
        id: "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
        label: "Denim",
        swatchColor: "denim"
      },
      "D6DB1C49-D913-4D7C-884B-1F7AF1F53187": {
        id: "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
        label: "Yellow",
        swatchColor: "yellow"
      },
      "199ABF77-578A-4EA8-8C2D-8EA080610A78": {
        id: "199ABF77-578A-4EA8-8C2D-8EA080610A78",
        label: "Dark Purple",
        swatchColor: "darkPurple"
      },
      "D9DAECFF-F3CE-4399-9690-5E045E8BFA49": {
        id: "D9DAECFF-F3CE-4399-9690-5E045E8BFA49",
        label: "Light Blue",
        swatchColor: "lightBlue"
      },
      "0CAFEDAF-A92E-4012-9E43-438B826F5FC7": {
        id: "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
        label: "Apple",
        swatchColor: "apple"
      },
      "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7": {
        id: "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
        label: "Red",
        swatchColor: "red"
      },
      "7F4245A2-AF3B-4633-BEBA-68494486C574": {
        id: "7F4245A2-AF3B-4633-BEBA-68494486C574",
        label: "Peach",
        swatchColor: "peach"
      }
    },
    allIds: [
      "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
      "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
      "199ABF77-578A-4EA8-8C2D-8EA080610A78",
      "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
      "7F4245A2-AF3B-4633-BEBA-68494486C574",
      "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
      "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
      "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
      "D9DAECFF-F3CE-4399-9690-5E045E8BFA49"
    ]
  },
  timelineViewSettings: {
    scrollBounds: "infinite",
    eventLayout: "clearAll",
    showIdentifier: false,
    showDates: true,
    showDuration: false,
    showNarrative: false,
    showRelationshipTypes: [],
    showImage: false,
    showSummary: false,
    showInfoIcons: false,
    showDependencies: true,
    zoomFrom: TimelineZoomLimits.Month,
    zoomTo: TimelineZoomLimits.Hour,
    showTypes: ["meeting", itemTypeIds.EVENT_ITEM_TYPE_ID, "sessionItem", "eventItem"]
  },
  narrativeViewSettings: {
    showIdentifier: false,
    showDates: false,
    showDuration: false,
    showNarrative: true,
    showRelatedAvatars: true,
    showImage: false,
    showSummary: false,
    showInfoIcons: false,
    columnLevel: 0
  },
  narrativeSettings: {
    labels: defaultGenericNarrativeLabels,
    autoDeleteWhenRemoveContainer: true,
    numbering: narrativeNumberingOptions.OUTLINE_STYLE
  },
  mindmapViewSettings: {
    showIdentifier: true,
    showDates: false,
    showDuration: false,
    showNarrative: false,
    showRelationshipTypes: [],
    showImage: false,
    showSummary: true,
    showInfoIcons: false
  },
  subwayViewSettings: {
    showDates: false,
    display: subwayDisplayOptions.SUBWAY
  },
  relationshipViewSettings: {
    showIdentifier: false,
    showDates: true,
    showDuration: false,
    showNarrative: false,
    showSummary: false,
    showInfoIcons: false
  },
  defaultColumns: defaultColumns
};
